import { useTheme } from '../context/ThemeContext';
import { ShieldCheck, Users, Zap, Award, Target, HeartHandshake, MapPin, Calendar, Clock, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function About() {
  const { colors } = useTheme();

  const values = [
    {
      icon: <Zap size={24} />,
      title: 'Accessibility & Convenience',
      desc: 'Instant 24/7 online booking with real-time slot availability for football, cricket, badminton, and more.',
    },
    {
      icon: <Users size={24} />,
      title: 'Vibrant Sports Community',
      desc: 'Connecting passionate players, local teams, and professional venues under one unified sports ecosystem.',
    },
    {
      icon: <Sparkles size={24} />,
      title: 'Tech-Driven Innovation',
      desc: 'Empowering turf owners with intelligent slot management, automated payouts, and real-time revenue analytics.',
    },
    {
      icon: <ShieldCheck size={24} />,
      title: 'Uncompromised Trust',
      desc: '100% verified turf listings, bank-grade encrypted payments, and dedicated 24/7 partner & player support.',
    },
  ];

  const features = [
    { title: 'Instant Confirmation', desc: 'No phone calls or waiting. Select your preferred slot, pay securely, and hit the ground playing.' },
    { title: 'Best Price Guarantee', desc: 'Transparent pricing with zero hidden charges. Exclusive promo codes and partner discounts available daily.' },
    { title: 'Top-Tier Facilities', desc: 'FIFA-approved artificial turfs, professional LED floodlighting, clean amenities, and parking facilities.' },
    { title: 'Flexible Cancellations', desc: 'Hassle-free rescheduling and quick refunds per venue policies directly from your user dashboard.' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: colors.background, color: colors.text }}>
      {/* Hero Section */}
      <section style={{
        padding: '6rem 1.5rem 5rem',
        background: 'linear-gradient(135deg, #084734 0%, #0a5c44 50%, #063527 100%)',
        position: 'relative', overflow: 'hidden', textAlign: 'center'
      }}>
        <div style={{
          position: 'absolute', top: '-40%', right: '-15%', width: '550px', height: '550px',
          borderRadius: '50%', background: 'rgba(206, 241, 123, 0.08)', pointerEvents: 'none',
          WebkitBackdropFilter: 'blur(40px)', backdropFilter: 'blur(40px)'
        }} />
        <div style={{
          position: 'absolute', bottom: '-30%', left: '-10%', width: '400px', height: '400px',
          borderRadius: '50%', background: 'rgba(206, 241, 123, 0.05)', pointerEvents: 'none'
        }} />
        <div style={{ maxWidth: '850px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '6px 18px', borderRadius: '50px', background: 'rgba(206, 241, 123, 0.15)',
            color: '#CEF17B', fontSize: '0.85rem', fontWeight: '700', marginBottom: '1.5rem',
            border: '1px solid rgba(206, 241, 123, 0.3)'
          }}>
            <Sparkles size={16} /> India's Premier Sports Venue Platform
          </div>
          <h1 style={{
            fontSize: 'clamp(2.2rem, 5vw, 3.6rem)', fontWeight: '900', color: '#fff',
            marginBottom: '1.5rem', lineHeight: 1.25, letterSpacing: '-0.02em'
          }}>
            Revolutionizing How India <span style={{ color: '#CEF17B' }}>Finds & Books Sports Turfs</span>
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.2rem)', color: 'rgba(255,255,255,0.85)',
            lineHeight: 1.8, maxWidth: '680px', margin: '0 auto'
          }}>
            TurfX connects passionate athletes, recreational players, and sports teams with top-rated turfs, 
            courts, and arenas across India — effortlessly and instantly.
          </p>
        </div>
      </section>

      {/* Our Story & Mission */}
      <section style={{ padding: '5rem 1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '3.5rem', alignItems: 'center'
        }}>
          <div>
            <div style={{
              fontSize: '0.8rem', fontWeight: '800', textTransform: 'uppercase',
              letterSpacing: '2px', color: '#084734', marginBottom: '0.5rem'
            }}>
              Our Mission & Vision
            </div>
            <h2 style={{
              fontSize: 'clamp(1.8rem, 3.5vw, 2.4rem)', fontWeight: '900',
              color: colors.text, marginBottom: '1.2rem', lineHeight: 1.25
            }}>
              Empowering Sports Communities Across <span style={{ color: '#084734' }}>Every Neighborhood</span>
            </h2>
            <div style={{ width: '60px', height: '4px', background: '#CEF17B', borderRadius: '2px', marginBottom: '1.8rem' }} />
            
            <p style={{ fontSize: '1.02rem', color: colors.textSecondary, lineHeight: 1.85, marginBottom: '1.2rem' }}>
              Founded with a mission to make active living hassle-free, TurfX was created to solve 
              the frustration of manual turf reservations, double-booked grounds, and unclear pricing.
            </p>
            <p style={{ fontSize: '1.02rem', color: colors.textSecondary, lineHeight: 1.85, marginBottom: '1.2rem' }}>
              Whether you are scheduling a late-night 5v5 football match, a weekend box cricket tournament, 
              or a morning badminton rally, TurfX brings full visibility to ground availability, ground specs, and amenities.
            </p>
            <p style={{ fontSize: '1.02rem', color: colors.textSecondary, lineHeight: 1.85 }}>
              For venue owners, our robust partner dashboard automates slot scheduling, offline ground bookings, 
              earnings payouts, and customer reviews — giving them complete control over their sports venue operations.
            </p>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #084734 0%, #0a5c44 100%)', borderRadius: '28px',
            padding: '3rem 2.2rem', color: '#fff', boxShadow: '0 20px 40px rgba(8,71,52,0.15)',
            position: 'relative', overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute', top: '-20px', right: '-20px', fontSize: '6rem',
              fontWeight: '900', color: 'rgba(206,241,123,0.15)', lineHeight: 1, pointerEvents: 'none'
            }}>&ldquo;</div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#CEF17B', marginBottom: '1rem' }}>
              Why We Play
            </h3>
            <p style={{ fontSize: '1.08rem', lineHeight: 1.8, fontStyle: 'italic', color: 'rgba(255,255,255,0.92)', marginBottom: '1.8rem' }}>
              "Sports bring people together like nothing else. We believe that booking a sports field should be as quick 
              and smooth as booking a cab. TurfX is built to keep India playing."
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#CEF17B', color: '#084734', fontWeight: '900', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                TX
              </div>
              <div>
                <div style={{ fontWeight: '800', color: '#CEF17B', fontSize: '1rem' }}>The TurfX Team</div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>MetaQode Technologies Pvt. Ltd.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section style={{
        padding: '5rem 1.5rem', background: colors.cardBg,
        borderTop: `1.5px solid ${colors.border}`, borderBottom: `1.5px solid ${colors.border}`
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: '900', color: colors.text, marginBottom: '0.75rem' }}>
              Driven By <span style={{ color: '#084734' }}>Core Values</span>
            </h2>
            <p style={{ color: colors.textSecondary, fontSize: '1.02rem', maxWidth: '620px', margin: '0 auto' }}>
              Our commitment to players and venue owners shapes every feature we design.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.8rem' }}>
            {values.map((v) => (
              <div key={v.title} style={{
                background: colors.background, borderRadius: '22px', padding: '2.2rem 1.8rem',
                border: `1.5px solid ${colors.border}`, transition: 'all 0.3s ease'
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.borderColor = '#084734'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = colors.border; }}
              >
                <div style={{
                  width: '52px', height: '52px', borderRadius: '16px', background: '#DCEFB8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem',
                  color: '#084734'
                }}>
                  {v.icon}
                </div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: colors.text, marginBottom: '0.75rem' }}>
                  {v.title}
                </h3>
                <p style={{ fontSize: '0.92rem', color: colors.textSecondary, lineHeight: 1.7, margin: 0 }}>
                  {v.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose TurfX Features */}
      <section style={{ padding: '5rem 1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
          <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: '900', color: colors.text, marginBottom: '0.75rem' }}>
            Why Players Choose <span style={{ color: '#084734' }}>TurfX</span>
          </h2>
          <p style={{ color: colors.textSecondary, fontSize: '1rem', maxWidth: '600px', margin: '0 auto' }}>
            Built for maximum reliability and convenience every time you play.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem' }}>
          {features.map((f, idx) => (
            <div key={f.title} style={{
              background: colors.cardBg, borderRadius: '20px', padding: '1.8rem',
              border: `1.5px solid ${colors.border}`, display: 'flex', flexDirection: 'column', gap: '0.75rem'
            }}>
              <div style={{
                fontSize: '0.85rem', fontWeight: '900', color: '#084734',
                background: '#DCEFB8', width: '32px', height: '32px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                0{idx + 1}
              </div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: '800', color: colors.text }}>{f.title}</h4>
              <p style={{ fontSize: '0.88rem', color: colors.textSecondary, lineHeight: 1.65, margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Call To Action */}
      <section style={{
        padding: '5rem 1.5rem', textAlign: 'center', background: 'linear-gradient(135deg, #084734 0%, #063527 100%)',
        color: '#fff', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ maxWidth: '650px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: '900', marginBottom: '1rem', color: '#fff' }}>
            Ready to Get on the <span style={{ color: '#CEF17B' }}>Ground</span>?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.05rem', marginBottom: '2.2rem', lineHeight: 1.7 }}>
            Discover top sports turfs around you, book your slot in seconds, and enjoy a premium playing experience.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/explore" style={{
              padding: '16px 38px', borderRadius: '50px', background: '#CEF17B',
              color: '#084734', fontWeight: '800', fontSize: '1rem', textDecoration: 'none',
              boxShadow: '0 8px 25px rgba(206,241,123,0.3)', transition: 'all 0.2s ease'
            }}>
              Explore Venues
            </Link>
            <Link to="/partner" style={{
              padding: '16px 34px', borderRadius: '50px', background: 'transparent',
              color: '#fff', fontWeight: '700', fontSize: '1rem', textDecoration: 'none',
              border: '2px solid rgba(255,255,255,0.3)', transition: 'all 0.2s ease'
            }}>
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
